/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentProps, ComponentType, ForwardedRef, forwardRef } from 'react';

import { Tooltip, TooltipProps } from './Tooltip';

export interface WithToolTipProps {
  tooltipContent?: TooltipProps['content'];
  tooltipArrow?: TooltipProps['arrow'];
  tooltipPosition?: TooltipProps['position'];
  tooltipDelayMilSeconds?: TooltipProps['delayMilSeconds'];
  tooltipUnstyled?: TooltipProps['unstyled'];
  tooltipContentOffset?: TooltipProps['contentOffset'];
  tooltipClickable?: TooltipProps['clickable'];
  tooltipDisabled?: boolean;
}

export type WithTooltip<T> = T & WithToolTipProps;

interface CompositionConfig {
  ensureTooltipPresence?: boolean;
  hasRef?: boolean;
  displayName?: string;
}

export function withToolTip<AnyComponent extends ComponentType<any>, RefType>(
  component: AnyComponent,
  { ensureTooltipPresence, hasRef, displayName }: CompositionConfig = {},
): (props: ComponentProps<AnyComponent> & WithToolTipProps) => JSX.Element {
  const WrapperWithToolTip = (
    {
      tooltipContent,
      tooltipArrow,
      tooltipPosition,
      tooltipDelayMilSeconds,
      tooltipUnstyled,
      tooltipContentOffset,
      tooltipDisabled,
      tooltipClickable,
      ...props
    }: ComponentProps<AnyComponent> & WithToolTipProps,
    ref: ForwardedRef<RefType>,
  ): JSX.Element => {
    const Component = component as (props: any) => JSX.Element;

    const base = <Component ref={hasRef ? ref : undefined} {...props} />;

    if (!tooltipContent) return <>{base}</>;

    return (
      <Tooltip
        content={tooltipContent}
        arrow={tooltipArrow}
        position={tooltipPosition}
        delayMilSeconds={tooltipDelayMilSeconds}
        ensurePresence={ensureTooltipPresence}
        unstyled={tooltipUnstyled}
        contentOffset={tooltipContentOffset}
        disabled={tooltipDisabled}
        clickable={tooltipClickable}
      >
        {base}
      </Tooltip>
    );
  };

  WrapperWithToolTip.displayName = displayName;

  // ComponentProps<XX> already picks up the ref props, our wrap here is correct
  return (hasRef ? forwardRef(WrapperWithToolTip as any) : WrapperWithToolTip) as (
    props: ComponentProps<AnyComponent> & WithToolTipProps,
  ) => JSX.Element;
}
